import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crown, Star, Gift, TrendingUp, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { promotionsAPI, servicesAPI } from '../../services/api';
import { Progress } from '../../components/ui/progress';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [promoRes, servicesRes] = await Promise.all([
        promotionsAPI.getActive(),
        servicesAPI.getAll({ featured_only: true }),
      ]);
      setPromotions(promoRes.data.promotions || []);
      setFeaturedServices(servicesRes.data.services?.slice(0, 4) || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierProgress = () => {
    const thresholds = { Bronze: 200, Silver: 500, Gold: 1000, Platinum: Infinity };
    const current = user?.lifetime_points || 0;
    const tier = user?.tier || 'Bronze';
    const next = thresholds[tier];
    
    if (tier === 'Platinum') return 100;
    
    const prevThreshold = tier === 'Bronze' ? 0 : tier === 'Silver' ? 200 : tier === 'Gold' ? 500 : 1000;
    const progress = ((current - prevThreshold) / (next - prevThreshold)) * 100;
    return Math.min(progress, 100);
  };

  const getNextTier = () => {
    const order = ['Bronze', 'Silver', 'Gold', 'Platinum'];
    const currentIndex = order.indexOf(user?.tier || 'Bronze');
    return currentIndex < 3 ? order[currentIndex + 1] : null;
  };

  const getPointsToNext = () => {
    const thresholds = { Bronze: 200, Silver: 500, Gold: 1000 };
    const next = getNextTier();
    if (!next) return 0;
    return Math.max(0, thresholds[user?.tier || 'Bronze'] - (user?.lifetime_points || 0));
  };

  const getTierColor = (tier) => {
    const colors = {
      Bronze: 'tier-bronze',
      Silver: 'tier-silver',
      Gold: 'tier-gold',
      Platinum: 'tier-platinum',
    };
    return colors[tier] || colors.Bronze;
  };

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <div className="skeleton h-48 rounded-2xl" />
        <div className="skeleton h-32 rounded-2xl" />
        <div className="grid grid-cols-2 gap-4">
          <div className="skeleton h-40 rounded-2xl" />
          <div className="skeleton h-40 rounded-2xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6 animate-fadeIn">
      {/* Points Card */}
      <div className="gradient-primary rounded-2xl p-6 text-white relative overflow-hidden" data-testid="points-card">
        <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <Star className="w-5 h-5 text-[#F5E6D3]" />
            <span className="text-white/80 text-sm">Current Points</span>
          </div>
          <p className="font-serif text-5xl mb-4">{user?.current_points || 0}</p>
          
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getTierColor(user?.tier)}`}>
              <Crown className="w-4 h-4 inline mr-1" />
              {user?.tier || 'Bronze'}
            </span>
            <span className="text-white/70 text-sm">
              {user?.lifetime_points || 0} lifetime points
            </span>
          </div>
        </div>
      </div>

      {/* Tier Progress */}
      {getNextTier() && (
        <div className="glass rounded-2xl p-5" data-testid="tier-progress">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#C9A689]" />
              <span className="font-medium text-[#4A154B]">Progress to {getNextTier()}</span>
            </div>
            <span className="text-sm text-gray-500">{getPointsToNext()} points to go</span>
          </div>
          <Progress value={getTierProgress()} className="h-3" />
        </div>
      )}

      {/* Active Promotions */}
      {promotions.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-serif text-xl text-[#4A154B] px-1">Active Promotions</h3>
          <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4">
            {promotions.map((promo) => (
              <div
                key={promo.id}
                className="flex-shrink-0 w-72 gradient-soft rounded-2xl p-5 border border-[#C9A689]/30"
                data-testid={`promotion-${promo.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-5 h-5 text-[#C9A689]" />
                  <span className="text-sm text-[#C9A689] font-medium uppercase tracking-wide">
                    {promo.promotion_type === 'multiplier' ? `${promo.value}x Points` : 'Bonus'}
                  </span>
                </div>
                <h4 className="font-serif text-lg text-[#4A154B] mb-1">{promo.name}</h4>
                <p className="text-sm text-gray-600 line-clamp-2">{promo.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/customer/services')}
          className="glass rounded-2xl p-5 text-left card-hover group"
          data-testid="view-services-btn"
        >
          <Sparkles className="w-10 h-10 text-[#C9A689] mb-3" />
          <h4 className="font-serif text-lg text-[#4A154B] mb-1">Our Services</h4>
          <p className="text-sm text-gray-500 mb-2">Browse & earn points</p>
          <ArrowRight className="w-5 h-5 text-[#C9A689] group-hover:translate-x-1 transition-transform" />
        </button>
        
        <button
          onClick={() => navigate('/customer/rewards')}
          className="glass rounded-2xl p-5 text-left card-hover group"
          data-testid="view-rewards-btn"
        >
          <Gift className="w-10 h-10 text-[#4A154B] mb-3" />
          <h4 className="font-serif text-lg text-[#4A154B] mb-1">Rewards</h4>
          <p className="text-sm text-gray-500 mb-2">Redeem your points</p>
          <ArrowRight className="w-5 h-5 text-[#C9A689] group-hover:translate-x-1 transition-transform" />
        </button>
      </div>

      {/* Featured Services */}
      {featuredServices.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <h3 className="font-serif text-xl text-[#4A154B]">Featured Services</h3>
            <button
              onClick={() => navigate('/customer/services')}
              className="text-sm text-[#C9A689] hover:text-[#4A154B] flex items-center gap-1"
            >
              View all <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {featuredServices.map((service) => (
              <div
                key={service.id}
                className="glass rounded-xl overflow-hidden card-hover"
                data-testid={`featured-service-${service.id}`}
              >
                <img
                  src={service.image_url || 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400'}
                  alt={service.name}
                  className="w-full h-24 object-cover"
                />
                <div className="p-3">
                  <h5 className="font-medium text-[#4A154B] text-sm truncate">{service.name}</h5>
                  <p className="text-[#C9A689] font-semibold">${service.price}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
